import type { StripeConnectInstance } from '@stripe/connect-js';
// `/pure` loads connect.js when called; the default entry loads it on import.
import { loadConnectAndInitialize } from '@stripe/connect-js/pure';
import { useCallback, useState } from 'react';

interface UseStripeConnectInstanceArgs {
  /**
   * Source varies per caller: the legacy vendor Stripe onboarding
   * (`setup-stripe.tsx`) keeps reading `import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY`;
   * the producer Selling-page onboarding passes `environment.stripeKey` (the
   * source every other Stripe.js flow in the app uses).
   */
  publishableKey?: string;
}

interface UseStripeConnectInstanceResult {
  stripeConnectInstance: StripeConnectInstance | undefined;
  /**
   * Initialize (or re-initialize) the embedded Connect instance against a
   * `fetchClientSecret` callback. No-ops — and sets `loadError` — when
   * `publishableKey` is missing.
   */
  initialize: (fetchClientSecret: () => Promise<string>) => void;
  loadError: string | undefined;
  setLoadError: (message: string | undefined) => void;
}

/**
 * Shared `loadConnectAndInitialize` wiring, extracted verbatim from
 * `setup-stripe.tsx` (the vendor/processor Stripe onboarding) so it can be
 * reused by the producer Selling-page onboarding without duplicating the
 * fonts/appearance options. Behavior-preserving: same defaults as before
 * extraction.
 * @param root0
 * @param root0.publishableKey
 */
export function useStripeConnectInstance({
  publishableKey,
}: UseStripeConnectInstanceArgs): UseStripeConnectInstanceResult {
  const [stripeConnectInstance, setStripeConnectInstance] =
    useState<StripeConnectInstance>();
  const [loadError, setLoadError] = useState<string>();

  const initialize = useCallback(
    (fetchClientSecret: () => Promise<string>) => {
      if (!publishableKey) {
        setLoadError('Stripe is not configured. Please contact support.');
        return;
      }

      setStripeConnectInstance(
        loadConnectAndInitialize({
          publishableKey,
          fetchClientSecret,
          fonts: [
            {
              cssSrc:
                'https://fonts.googleapis.com/css2?family=Poppins:wght@400&display=swap',
            },
          ],
          appearance: {
            overlays: 'drawer',
            variables: {
              fontFamily: 'Poppins',
              colorPrimary: '#006f35',
            },
          },
        }),
      );
    },
    [publishableKey],
  );

  return { stripeConnectInstance, initialize, loadError, setLoadError };
}
